import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { googleCallback } from '../services/auth';
import { getOnboardingStatus } from '../services/api';

function GoogleCallback({ setUser }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const redirectTo = searchParams.get('state'); // Google returns our state parameter as 'state'

      if (error) {
        setError('Google authentication failed: ' + error);
        setLoading(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        setLoading(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        const result = await googleCallback(code);
        setUser(result.user);

        // Check if user needs to complete onboarding
        try {
          const onboardingData = await getOnboardingStatus();
          const membership = onboardingData.membership;

          // Determine redirect based on onboarding status
          if (membership?.onboarding_status === 'completed') {
            // User has completed onboarding, go to intended destination or dashboard
            const destination = redirectTo || '/dashboard';
            navigate(destination, { replace: true });
          } else if (membership?.onboarding_status === 'payment_pending') {
            // User has selected a paid plan but hasn't completed payment
            // Preserve redirect destination through checkout
            const checkoutUrl = redirectTo ? `/checkout?redirect=${encodeURIComponent(redirectTo)}` : '/checkout';
            navigate(checkoutUrl, {
              replace: true,
              state: { membership: membership }
            });
          } else {
            // User needs to complete plan selection or other onboarding steps
            // Preserve redirect destination through onboarding
            const onboardingUrl = redirectTo ? `/onboarding?redirect=${encodeURIComponent(redirectTo)}` : '/onboarding';
            navigate(onboardingUrl, {
              replace: true,
              state: {
                membership: membership || result.membership || null
              }
            });
          }
        } catch (onboardingError) {
          console.error('Error checking onboarding status:', onboardingError);
          // If we can't check onboarding status, default to onboarding page
          // Preserve redirect destination
          const onboardingUrl = redirectTo ? `/onboarding?redirect=${encodeURIComponent(redirectTo)}` : '/onboarding';
          navigate(onboardingUrl, {
            replace: true,
            state: {
              membership: result.membership || null
            }
          });
        }
      } catch (err) {
        setError(err.message || 'Failed to complete Google authentication');
        setLoading(false);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setUser]);

  return (
    <div className="min-h-full flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <h2 className="mt-6 text-xl font-semibold text-gray-900">
                Completing Google sign in...
              </h2>
            </>
          ) : error ? (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="mt-6 text-xl font-semibold text-gray-900">
                Authentication failed
              </h2>
              <p className="mt-2 text-sm text-gray-600">{error}</p>
              <p className="mt-4 text-sm text-gray-500">Redirecting to login...</p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default GoogleCallback;
